import React, { useEffect, useRef } from 'react';
import { handleRequest } from '@/utils';
import { addDialogue } from '@/utils/sessionUtils';
import { InitStoreType } from '@/types/store/init';
import { TRACKING_TYPE, TRACKING_OPERATION, TRACKING_SECTION, INPUT_TYPE, TRACKING_TARGET } from '@/utils/constants';
import { dispatchTrack } from '@/utils/tracking';
import { TrackerProps, ChatbotUIBasicProps } from '@/types/common';
import Msg from '@/components/Msg';
import Drawer from '@/components/Drawer';
import './style.scss';
import { handleClickHistoryButton } from '@/utils/tools';
import { getChildInject, isNum } from '@/utils/instantLoaded/tools';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { inject, observer } from 'mobx-react';

const PREFIX = 'order-selection-faq-container';

interface OrderFaqItemBase {
  node_id?: string;
  button_id: string;
  button_text: string;
  intent_id?: number;
  source_intent_id?: number;
  ext_key_value?: any;
  action?: any;
  [key: string]: any;
}

interface ButtonInfos {
  button_id: string;
  button_type: string;
  text_value: string;
}

interface OrderFaqProps extends TrackerProps {
  title: string;
  listTitle: string;
  displayNum: number;
  listItems: Array<OrderFaqItemBase>;
  showMoreButton: ButtonInfos;
  initStore?: InitStoreType;
  dialogueId: string;
  historyDialogueId?: string;
  historySessionId?: string;
  isHistory: boolean;
  nodePointId: string;
  isTFE?: boolean;
  threadId?: string;
  threadNodeId?: string;
  threadIdV2?: string;
  roundId?: string;
  customCallback?: ChatbotUIBasicProps['customCallback'];
}

const OrderSelectionFaq = (props: OrderFaqProps) => {
  const {
    title,
    listTitle,
    displayNum,
    listItems = [],
    showMoreButton,
    isHistory,
    dialogueId,
    historyDialogueId,
    historySessionId,
    initStore,
    nodePointId,
  } = props;
  const [showMoreList, setShowMoreList] = React.useState(false);
  const myRef = useRef<Array<HTMLDivElement | null>>([]);

  // 列表上报
  const trackingname = TRACKING_TYPE.ORDER_SELECTION_FAQ_LIST;
  const drawerTrackingname = TRACKING_TYPE.ORDER_SELECTION_FAQ_DRAWER_LIST;
  const trackingdataBase = {
    chat_session_id: isHistory ? historySessionId : initStore?.sessionId,
    chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
    node_point_id: nodePointId,
  };

  useEffect(() => {
    addObserver(myRef.current);

    return () => {
      removeObserver(myRef.current);
    };
  }, []);

  const handleShowMoreBtn = () => {
    if (isHistory) {
      handleClickHistoryButton();
      return;
    }

    setShowMoreList(true);

    const viewed_objects: any[] = [];
    listItems.forEach((item, index) => {
      viewed_objects.push({
        ...trackingdataBase,
        source_intent_id: item?.source_intent_id,
        intent_id: item?.intent_id,
        location: index,
        content: item.button_text,
      });
    });

    dispatchTrack({
      operation: TRACKING_OPERATION.IMPRESSION,
      section: TRACKING_SECTION[drawerTrackingname],
      target: TRACKING_TARGET[drawerTrackingname],
      data: { viewed_objects },
    });
  };

  const handleCloseModal = () => {
    setShowMoreList(false);
  };

  const handleClick = (button: any, closeModal = false, trackData = {}) => {
    const { isHistory } = props;

    if (isHistory) {
      handleClickHistoryButton();
      return;
    }

    if (closeModal) {
      handleCloseModal();
    }

    const content = button.button_text;
    const curPos = addDialogue({
      from: 'user',
      type: 'Msg',
      data: { content },
    });

    if (isNum(curPos)) {
      requestMessage(button);
    }

    // 上报 click 事件
    dispatchTrack({
      operation: TRACKING_OPERATION.CLICK,
      ...trackData,
    });
  };

  const requestMessage = (item: OrderFaqItemBase) => {
    const { initStore, dialogueId, threadId, threadNodeId, threadIdV2, roundId, customCallback = {} } = props;
    handleRequest('postMessage', {
      session_id: initStore?.sessionId,
      dialogue_id: dialogueId,
      input_type: INPUT_TYPE.BUTTON,
      message_text: item.button_text,
      thread_id: threadId,
      thread_node_id: threadNodeId,
      thread_id_v2: threadIdV2,
      round_id: roundId,
      is_bypass: item?.action?.is_bypass,
      message_extra: {
        node_id: item.node_id,
        ext_key_value: item.ext_key_value,
        task_bot_action: item.action?.task_bot_action,
        params: { order_id: 0 },
      },
      ...customCallback,
    });
  };

  return (
    <div className={PREFIX}>
      <Msg className="message-content" content={title} />

      <div className={`${PREFIX}-list`}>
        {listItems.slice(0, displayNum).map((item, index) => {
          return (
            <div
              key={index}
              ref={(el) => {
                myRef.current[index] = el;
              }}
              data-trackingname={trackingname}
              data-trackingdata={JSON.stringify({
                ...trackingdataBase,
                source_intent_id: item?.source_intent_id,
                intent_id: item?.intent_id,
                location: index,
                content: item.button_text,
              })}
            >
              <div
                className={`${PREFIX}-item`}
                onClick={() =>
                  handleClick(item, false, {
                    section: TRACKING_SECTION[trackingname],
                    target: TRACKING_TARGET[trackingname],
                    data: {
                      ...trackingdataBase,
                      source_intent_id: item?.source_intent_id,
                      intent_id: item?.intent_id,
                      location: index,
                      content: item.button_text,
                    },
                  })
                }
              >
                {item.button_text}
              </div>

              <div className={`${PREFIX}-divider`} />
            </div>
          );
        })}
      </div>

      {listItems.length > displayNum && (
        <div className="show-more-btn" onClick={handleShowMoreBtn}>
          {showMoreButton?.text_value}
        </div>
      )}

      <Drawer onClose={handleCloseModal} height="auto" show={showMoreList}>
        <div className="drawer-order-faq-container">
          <div className="drawer-order-faq-title">{listTitle}</div>

          <div className="drawer-order-faq-list">
            {listItems.map((item, index) => {
              return (
                <div
                  className="item"
                  key={item.button_id}
                  onClick={() => {
                    handleClick(item, true, {
                      section: TRACKING_SECTION[drawerTrackingname],
                      target: TRACKING_TARGET[drawerTrackingname],
                      data: {
                        ...trackingdataBase,
                        source_intent_id: item?.source_intent_id,
                        intent_id: item?.intent_id,
                        location: index,
                        content: item.button_text,
                      },
                    });
                  }}
                >
                  {item.button_text}
                </div>
              );
            })}
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default inject(getChildInject('initStore'))(observer(OrderSelectionFaq));
